import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { buildReturnStatusMap } from "../../utils/orderStatus";
import { useModal } from "../../context/ModalContext";
import CSSelect from "../../components/CSSelect";

const API = process.env.REACT_APP_API_URL || "";

// 마이페이지 팔레트에 맞춘 차분한 상태색 (관리자 화면은 utils/orderStatus 그대로)
const STATUS_COLORS = {
  pending: "#C8813F",
  approved: "#6F8A5B",
  collecting: "#A0673A",
  collected: "#5F7F86",
  completed: "#6F8A5B",
  rejected: "#B4543A",
  withdrawn: "#9C8B7C",
};

const REASONS_BY_TYPE = {
  cancel: [
    { key: "changeMind", value: "단순 변심" },
    { key: "optionChange", value: "옵션 변경" },
    { key: "reorder", value: "다른 상품 주문 예정" },
    { key: "other", value: "기타" },
  ],
  refund: [
    { key: "defect", value: "상품 불량 / 파손" },
    { key: "wrongItem", value: "주문과 다른 상품 수령" },
    { key: "damaged", value: "배송 중 파손" },
    { key: "other", value: "기타" },
  ],
  exchange: [
    { key: "defect", value: "상품 불량 / 파손" },
    { key: "wrongItem", value: "주문과 다른 상품 수령" },
    { key: "damaged", value: "배송 중 파손" },
    { key: "changeMind", value: "단순 변심" },
    { key: "other", value: "기타" },
  ],
};

// 기존 데이터 호환용
const REASON_KEY_MAP = {
  "상품 불량 / 파손": "defect",
  "주문과 다른 상품 수령": "wrongItem",
  "단순 변심": "changeMind",
  "옵션 변경": "optionChange",
  "다른 상품 주문 예정": "reorder",
  "배송 중 파손": "damaged",
  "기타": "other",
};

export default function MyPageReturns() {
  const { t, i18n } = useTranslation();
  const { showAlert, showConfirm } = useModal();
  const STATUS_MAP = buildReturnStatusMap(t);
  const { user } = useContext(AuthContext);
  const [returns, setReturns] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [form, setForm] = useState({ order_id: "", type: "refund", reason: "", detail: "", returnMethod: "self" });
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  const [submitting, setSubmitting] = useState(false);

  // 주문 내역에서 신청 버튼 클릭 시 자동 오픈
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) {
        setShowForm(true);
        setForm((prev) => ({
          ...prev,
          type: e.detail.type || "refund",
          order_id: e.detail.orderId || "",
        }));
      }
    };
    window.addEventListener("mypage:openReturnForm", handler);
    return () => window.removeEventListener("mypage:openReturnForm", handler);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      fetch(`${API}/api/my-returns?user_id=${user.id}`).then(r => r.json()),
      fetch(`${API}/api/my-orders?user_id=${user.id}`).then(r => r.json()),
    ]).then(([rets, ords]) => {
      const list = Array.isArray(rets) ? rets : [];
      setReturns(list);
      setFiltered(list);
      setOrders(Array.isArray(ords) ? ords : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    let list = returns;
    if (dateFrom) list = list.filter((r) => r.created_at >= dateFrom);
    if (dateTo) list = list.filter((r) => r.created_at <= dateTo + "T23:59:59");
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      list = list.filter((r) => (r.orders?.order_number || "").toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [dateFrom, dateTo, searchValue, returns]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.order_id || !form.reason) return showAlert(t("mypage.returns.alertSelectOrderReason"));
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/returns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, user_id: user.id, photos }),
      });
      if (res.ok) {
        const needAccount = form.returnMethod === "pickup" && form.reason === "단순 변심";
        if (needAccount) {
          setShowAccountModal(true);
        } else {
          showAlert(t("mypage.returns.alertSubmitSuccess"));
        }
        setShowForm(false);
        setForm({ order_id: "", type: "refund", reason: "", detail: "", returnMethod: "self" });
        setPhotos([]);
        const rets = await fetch(`${API}/api/my-returns?user_id=${user.id}`).then(r => r.json());
        setReturns(Array.isArray(rets) ? rets : []);
      } else {
        showAlert(t("mypage.returns.alertSubmitFailed"));
      }
    } catch { showAlert(t("mypage.returns.alertError")); }
    finally { setSubmitting(false); }
  }

  async function handleWithdraw(returnId) {
    if (!(await showConfirm(t("mypage.returns.withdrawConfirm")))) return;
    try {
      const res = await fetch(`${API}/api/returns/${returnId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "withdrawn" }),
      });
      if (res.ok) {
        showAlert(t("mypage.returns.withdrawSuccess"));
        const rets = await fetch(`${API}/api/my-returns?user_id=${user.id}`).then(r => r.json());
        setReturns(Array.isArray(rets) ? rets : []);
      } else {
        showAlert(t("mypage.returns.withdrawFailed"));
      }
    } catch { showAlert(t("mypage.returns.alertError")); }
  }

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 5) {
      showAlert(t("mypage.orders.maxPhotosError"));
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("user_id", user.id);
        const res = await fetch(`${API}/api/returns/upload-photo`, { method: "POST", body: fd });
        const data = await res.json();
        if (data.url) uploaded.push(data.url);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch {
      showAlert(t("mypage.orders.uploadFailed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  // 유형에 따라 선택 가능한 주문 필터
  const eligibleOrders = form.type === "cancel"
    ? orders.filter(o => ["confirmed", "printing", "printed"].includes(o.status))
    : orders.filter(o => o.status === "delivered");

  return (
    <div className="mypage-card mp-returns">
      <div className="mp-returns-title-row">
        <h2>{t("mypage.returns.title")}</h2>
        <span className="mp-returns-count">{returns.length}{t("mypage.orders.countSuffix")}</span>
        <button className="mp-return-new-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? t("mypage.returns.cancel") : t("mypage.returns.newRequest")}
        </button>
      </div>
      <p className="mypage-desc">{t("mypage.returns.desc")}</p>

      {/* 필터 */}
      <div className="mp-orders-filter">
        <label>
          {t("mypage.orders.dateFrom")}
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label>
          {t("mypage.orders.dateTo")}
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label>
          {t("mypage.orders.orderNumber")}
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("mypage.orders.orderNumber")}
          />
        </label>
        {(dateFrom || dateTo || searchValue) && (
          <button className="mp-filter-reset" onClick={() => { setDateFrom(""); setDateTo(""); setSearchValue(""); }}>{t("mypage.orders.reset")}</button>
        )}
        <span className="mp-filter-count">{filtered.length}{t("mypage.orders.countSuffix")}</span>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#7C6655" }}>{t("mypage.common.loading")}</div>
      ) : (
      <>
      {/* 신청 폼 */}
      {showForm && (
        <form className="mp-return-form" onSubmit={handleSubmit}>
          <div className="mp-return-policy-notice">
            <strong>{t("mypage.returns.policyNoticeTitle")}</strong>
            <ul>
              <li>{t("mypage.returns.policyNoticeDigital")}</li>
              <li>{t("mypage.returns.policyNoticeSubscription")}</li>
              <li>{t("mypage.returns.policyNoticePrinting")}</li>
            </ul>
            <a href="/terms" target="_blank" rel="noreferrer">
              {t("mypage.returns.policyNoticeLink")}
            </a>
          </div>
          <label>
            {t("mypage.returns.selectOrder")}
            <CSSelect
              block
              required
              value={form.order_id}
              onChange={(v) => setForm({ ...form, order_id: v })}
              placeholder={t("mypage.returns.selectOrderPlaceholder")}
              options={eligibleOrders.map((o) => ({
                value: o.id,
                label: `${o.order_number || o.id.slice(0, 8)} — ${((o.total_price || 0) + (o.shipping_cost || 0)).toLocaleString()}${t("mypage.orders.currencyWon")}`,
              }))}
            />
            {eligibleOrders.length === 0 && (
              <p className="mp-return-note">
                {form.type === "cancel"
                  ? t("mypage.returns.cancelEligibleNote")
                  : t("mypage.returns.deliveredOnlyNote")}
              </p>
            )}
          </label>
          <label>
            {t("mypage.returns.type")}
            <div className="mp-return-type-toggle">
              <button type="button" className={form.type === "cancel" ? "active" : ""} onClick={() => setForm({ ...form, type: "cancel", reason: "" })}>{t("mypage.orders.cancelRequest")}</button>
              <button type="button" className={form.type === "refund" ? "active" : ""} onClick={() => setForm({ ...form, type: "refund", reason: "" })}>{t("mypage.returns.refund")}</button>
              <button type="button" className={form.type === "exchange" ? "active" : ""} onClick={() => setForm({ ...form, type: "exchange", reason: "" })}>{t("mypage.returns.exchange")}</button>
            </div>
          </label>
          <label>
            {t("mypage.returns.reason")}
            <CSSelect
              block
              required
              value={form.reason}
              onChange={(v) => setForm({ ...form, reason: v })}
              placeholder={t("mypage.returns.selectReasonPlaceholder")}
              options={(REASONS_BY_TYPE[form.type] || []).map((r) => ({
                value: r.value,
                label: t(`mypage.returns.reasons.${r.key}`),
              }))}
            />
          </label>
          <label>
            {t("mypage.returns.detail")}
            <textarea value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder={t("mypage.returns.detailPlaceholder")} rows={3} />
          </label>
          {(form.type === "exchange" || form.type === "refund") && (
          <label>
            {t("mypage.returns.returnMethod")}
            <div className="mp-return-type-toggle">
              <button type="button" className={form.returnMethod === "self" ? "active" : ""} onClick={() => setForm({ ...form, returnMethod: "self" })}>{t("mypage.returns.selfShip")}</button>
              <button type="button" className={form.returnMethod === "pickup" ? "active" : ""} onClick={() => setForm({ ...form, returnMethod: "pickup" })}>{t("mypage.returns.pickupRequest")}</button>
            </div>
            {form.returnMethod === "pickup" && form.reason === "단순 변심" && (
              <p className="mp-return-note" style={{ color: "#B4543A" }}>
                {t("mypage.returns.pickupCostNotice")}
              </p>
            )}
          </label>
          )}
          {form.type !== "cancel" && (
          <label>
            {t("mypage.orders.photos")} ({photos.length}/5)
            <div className="mp-photo-list">
              {photos.map((url, i) => (
                <div key={i} className="mp-photo-item">
                  <img src={url} alt="" />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="mp-photo-add">
                  <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} disabled={uploading} />
                  {uploading ? "..." : "+"}
                </label>
              )}
            </div>
            <p className="mp-return-note">{t("mypage.orders.photosHint")}</p>
          </label>
          )}
          <button type="submit" className="mp-return-submit" disabled={submitting || uploading}>
            {submitting ? t("mypage.returns.submitting") : t("mypage.returns.submit")}
          </button>
        </form>
      )}

      {/* 신청 목록 */}
      {filtered.length === 0 && !showForm ? (
        <div className="mp-returns-empty">{t("mypage.returns.empty")}</div>
      ) : (
        <div className="mp-returns-list">
          {filtered.map((r) => {
            const rtype = r.request_type || r.type;
            const typeLabel = rtype === "cancel" ? t("mypage.orders.cancelRequest")
              : rtype === "exchange" ? t("mypage.orders.exchangeRequest")
              : t("mypage.orders.returnRequest");
            const typeColor = rtype === "cancel" ? "#C8813F" : rtype === "exchange" ? "#5A3825" : "#B4543A";

            const isExpanded = expandedIds.has(r.id);

            return (
              <div key={r.id} className={`mp-return-card${isExpanded ? " expanded" : ""}`}>
                {/* 헤더: 날짜 + 유형 + 상태 */}
                <div
                  className="mp-return-card-header"
                  onClick={() => toggleExpand(r.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="mp-return-meta">
                    <span className="mp-return-date-big">
                      {new Date(r.created_at).toLocaleDateString(i18n.language)}
                    </span>
                    <span className="mp-return-order-num">
                      {t("mypage.returns.orderLabel")} {r.orders?.order_number || "-"}
                    </span>
                  </div>
                  <div className="mp-return-badges">
                    <span
                      className="mp-return-type-badge"
                      style={{ background: `${typeColor}1F`, color: typeColor, border: `1px solid ${typeColor}33` }}
                    >
                      {typeLabel}
                    </span>
                    <span
                      className="mp-return-status-badge"
                      style={{
                        background: `${STATUS_COLORS[r.status]}1F`,
                        color: STATUS_COLORS[r.status],
                        border: `1px solid ${STATUS_COLORS[r.status]}33`,
                      }}
                    >
                      {t(`mypage.returns.status.${r.status}`, STATUS_MAP[r.status] || r.status)}
                    </span>
                    <span className="mp-return-arrow">{isExpanded ? "▴" : "▾"}</span>
                  </div>
                </div>

                {isExpanded && (
                <>
                {/* 요약 정보 */}
                <div className="mp-return-summary">
                  <div className="mp-return-summary-item">
                    <span className="mp-return-label">{t("mypage.returns.reasonLabel")}</span>
                    <span className="mp-return-value">
                      {REASON_KEY_MAP[r.reason] ? t(`mypage.returns.reasons.${REASON_KEY_MAP[r.reason]}`) : r.reason}
                    </span>
                  </div>
                  {r.detail && (
                    <div className="mp-return-summary-item">
                      <span className="mp-return-label">{t("mypage.returns.detailLabel")}</span>
                      <span className="mp-return-value">{r.detail}</span>
                    </div>
                  )}
                </div>

                {/* 거절 사유 */}
                {r.status === "rejected" && r.reject_reason && (
                  <div className="mp-return-reject-box">
                    <strong>{t("mypage.returns.rejectReasonLabel")}:</strong> {r.reject_reason}
                  </div>
                )}

                {/* 배송비 부담 */}
                {rtype !== "cancel" && r.shipping_cost_bearer && (
                  <div className="mp-return-summary" style={{ paddingTop: 0 }}>
                    <div className="mp-return-summary-item">
                      <span className="mp-return-label">{t("mypage.returns.shippingBearer")}</span>
                      <span className="mp-return-value">
                        {r.shipping_cost_bearer === "seller" ? t("mypage.returns.bearerSeller") : t("mypage.returns.bearerCustomer")}
                      </span>
                    </div>
                  </div>
                )}

                {/* 배송비 입금 안내 (단순변심 + 수거 요청) */}
                {r.reason === "단순 변심" && r.return_method === "pickup" && ["pending", "approved"].includes(r.status) && (
                  <div style={{ padding: "14px 24px", background: "#FBF6EE", borderTop: "1px solid #E7DAC8", fontSize: 13, color: "#5A3825" }}>
                    <strong>{t("mypage.returns.accountTitle")}</strong>
                    <div style={{ marginTop: 8, background: "#fff", border: "1px solid #E7DAC8", borderRadius: 8, padding: "10px 14px" }}>
                      <div>{t("mypage.returns.bank")}: 기업은행</div>
                      <div>{t("mypage.returns.accountNum")}: 396-058820-01-037</div>
                      <div>{t("mypage.returns.accountHolder")}: (주)쓰리디메이커즈</div>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: "#A0673A" }}>{t("mypage.returns.accountNote")}</div>
                  </div>
                )}

                {/* 송장 정보 */}
                {(r.return_tracking_number || r.new_tracking_number || r.admin_memo) && (
                  <div className="mp-return-tracking-info">
                    {r.return_tracking_number && (
                      <div className="mp-return-summary-item">
                        <span className="mp-return-label">{t("mypage.returns.returnTracking")}</span>
                        <span className="mp-return-value mp-return-mono">{r.return_tracking_number}</span>
                      </div>
                    )}
                    {r.new_tracking_number && (
                      <div className="mp-return-summary-item">
                        <span className="mp-return-label">{t("mypage.returns.newTracking")}</span>
                        <span className="mp-return-value mp-return-mono">{r.new_tracking_number}</span>
                      </div>
                    )}
                    {r.admin_memo && (
                      <div className="mp-return-summary-item mp-return-admin-memo">
                        <span className="mp-return-label">{t("mypage.returns.adminMemo")}</span>
                        <span className="mp-return-value">{r.admin_memo}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* 첨부 사진 */}
                {r.photos && Array.isArray(r.photos) && r.photos.length > 0 && (
                  <div className="mp-return-photos">
                    {r.photos.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt="" />
                      </a>
                    ))}
                  </div>
                )}

                {/* 신청 철회 (pending만) */}
                {r.status === "pending" && (
                  <div className="mp-return-withdraw">
                    <button
                      className="mp-return-withdraw-btn"
                      onClick={() => handleWithdraw(r.id)}
                    >
                      {t("mypage.returns.withdraw")}
                    </button>
                  </div>
                )}
                </>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* 배송비 입금 안내 모달 */}
      {showAccountModal && (
        <div className="mp-modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="mp-alert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mp-alert-icon" style={{ background: "#C8813F" }}>!</div>
            <div className="mp-alert-message">
              <strong>{t("mypage.returns.accountTitle")}</strong>
              <br /><br />
              {t("mypage.returns.accountDesc")}
              <br /><br />
              <div style={{ background: "#FBF6EE", border: "1px solid #E7DAC8", borderRadius: 8, padding: "14px 18px", textAlign: "left", fontSize: 14 }}>
                <div><strong>{t("mypage.returns.bank")}</strong> 기업은행</div>
                <div><strong>{t("mypage.returns.accountNum")}</strong> 396-058820-01-037</div>
                <div><strong>{t("mypage.returns.accountHolder")}</strong> (주)쓰리디메이커즈</div>
              </div>
              <br />
              <span style={{ fontSize: 12, color: "#7C6655" }}>{t("mypage.returns.accountNote")}</span>
            </div>
            <button className="mp-alert-btn" style={{ background: "#C8813F" }} onClick={() => setShowAccountModal(false)}>
              {t("common.confirm") || "확인"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
